import { Calendar, Briefcase, Users, CheckCircle } from 'lucide-react';

const HowWeWorkFinal = () => {
  const engagementModels = [
    {
      title: "Project-Based",
      icon: Briefcase,
      subtitle: "Targeted support for specific product challenges",
      description: "Get rapid clarity on critical decisions or validate key assumptions before major investment. Perfect for early-stage teams needing quick wins and strategic direction.",
      whatMakesThisWork: "I help you understand your customers and clarify your product vision through lightweight prototypes and demos. This gives your team confidence about what to build and why, setting the foundation for more detailed requirements work later.",
      deliverables: [
        "Customer persona definition and journey mapping",
        "Experience flow audits with concept prototypes",
        "MVP feature prioritization based on user validation",
        "Validated assumptions and strategic direction",
        "User research insights and strategy documents"
      ],
      timeline: "2-6 weeks"
    },
    {
      title: "Short-Term Engagements", 
      icon: Calendar,
      subtitle: "Strategic shifts with engineering-ready outputs",
      description: "Deeper partnership for companies refining direction, launching new features, or entering new markets. Focused engagement with clear handoff.",
      whatMakesThisWork: "I dive deep on your specific challenge, validate the approach through working demos, then deliver comprehensive requirements for your engineering team. Focused engagement with clear handoff.",
      deliverables: [
        "Refined product strategy with prototype validation",
        "Go-to-market plans with concept demonstrations", 
        "Tool and platform evaluations with hands-on analysis",
        "Detailed engineering specifications with visual context",
        "Strategic frameworks and stakeholder alignment materials"
      ],
      timeline: "1-3 months"
    },
    {
      title: "Longer-Term Support",
      icon: Users, 
      subtitle: "Fractional product leadership with execution muscle",
      description: "True fractional Head of Product partnership. I embed with your team to provide ongoing strategic guidance while building and validating solutions that set engineering up for success.",
      whatMakesThisWork: "I work as your ongoing product partner, continuously validating new features and requirements through multiple build cycles. Embedded relationship that scales with your product needs.",
      deliverables: [
        "Feature specifications through iterative prototyping and testing",
        "Cross-functional team partnership and alignment",
        "Continuous user feedback integration into roadmap", 
        "Sprint planning support and iterative feedback cycles",
        "Strategic roadmap updates and engineering documentation"
      ],
      timeline: "3+ months"
    }
  ];

  return (
    <section id="how-we-work" className="section bg-white py-16 md:py-20">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-darkText">
            How I <span className="text-cyanPrimary">Work</span>
          </h2>
          <p className="text-lg text-darkText/80 leading-relaxed">
            I offer flexible engagement models tailored to your specific needs. Whether you're defining your first MVP or scaling thoughtfully, I plug in exactly where you need support most.
          </p>
        </div>

        {/* Engagement Models */}
        <div className="space-y-16">
          {engagementModels.map((model, index) => (
            <div key={index} className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              
              {/* Left Column - Content */}
              <div className="space-y-6">
                {/* Header with circular icon like original */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-cyan-400 rounded-full flex items-center justify-center">
                      <model.icon className="text-white" size={20} />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-darkText">{model.title}</h3>
                      <p className="text-darkText/70">{model.subtitle}</p>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <p className="text-darkText/80 leading-relaxed">
                  {model.description}
                </p>

                {/* What Makes This Work */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-darkText">What makes this work:</h4>
                  <p className="text-darkText/80 leading-relaxed">
                    {model.whatMakesThisWork}
                  </p>
                </div>
              </div>

              {/* Right Column - Deliverables */}
              <div className="bg-grayLight/50 rounded-2xl p-6">
                <h4 className="font-semibold text-darkText mb-4 flex items-center gap-2">
                  <CheckCircle className="text-cyanPrimary" size={18} />
                  Typical deliverables include:
                </h4>
                
                <div className="space-y-3 mb-6">
                  {model.deliverables.map((deliverable, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-cyanPrimary rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-darkText/80 text-sm leading-relaxed">{deliverable}</span>
                    </div>
                  ))}
                </div>

                {/* Timeline moved to bottom of grey box */}
                <div className="pt-4 border-t border-grayMedium/30">
                  <div className="flex items-center gap-2 text-darkText/70">
                    <Calendar size={16} />
                    <span className="font-medium">Timeline: {model.timeline}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Why This Approach Works - With better separation */}
        <div className="mt-32">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h3 className="text-3xl md:text-4xl font-bold text-darkText mb-6">
              Why This Approach <span className="text-cyanPrimary">Works</span>
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white rounded-xl p-8 shadow-soft border border-grayMedium/20 text-center">
              <h4 className="font-semibold text-darkText mb-4 text-lg">For Business Teams</h4>
              <p className="text-darkText/80 leading-relaxed">
                See exactly what you're building before committing engineering resources. No more "is this what you meant?" conversations.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-soft border border-grayMedium/20 text-center">
              <h4 className="font-semibold text-darkText mb-4 text-lg">For Engineering Teams</h4>
              <p className="text-darkText/80 leading-relaxed">
                Get validated, specific requirements with working prototypes as reference. Spend time building, not guessing at business intent.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-soft border border-grayMedium/20 text-center">
              <h4 className="font-semibold text-darkText mb-4 text-lg">For Users</h4>
              <p className="text-darkText/80 leading-relaxed">
                Experience solutions that actually solve real problems because they've been tested and refined before full development.
              </p>
            </div>
          </div>
        </div>

        {/* Methodology Section - Option 1: Clean Linear Flow */}
        <div className="mt-32 bg-grayLight py-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-darkText mb-8">
                My <span className="text-cyanPrimary">Methodology</span>
              </h3>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-soft">
              <p className="text-lg text-darkText/80 leading-relaxed mb-8 text-center">
                Each short and long-term engagement follows a proven iterative process designed to reduce risk and deliver clear outcomes:
              </p>
              
              {/* Clean linear flow */}
              <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
                <span className="text-cyanPrimary font-semibold px-4 py-2 bg-cyanPrimary/10 rounded-lg text-center">
                  Stakeholder Alignment
                </span>
                <span className="text-cyanPrimary text-xl hidden sm:inline">→</span>
                <span className="text-cyanPrimary font-semibold px-4 py-2 bg-cyanPrimary/10 rounded-lg text-center">
                  Strategic Foundation
                </span>
                <span className="text-cyanPrimary text-xl hidden sm:inline">→</span>
                <span className="text-cyanPrimary font-semibold px-4 py-2 bg-cyanPrimary/10 rounded-lg text-center">
                  Rapid Validation
                </span>
                <span className="text-cyanPrimary text-xl hidden sm:inline">→</span>
                <span className="text-cyanPrimary font-semibold px-4 py-2 bg-cyanPrimary/10 rounded-lg text-center">
                  Execution-Ready Deliverables
                </span>
              </div>
              
              <p className="text-sm text-darkText/70 leading-relaxed text-center">
                Project-based work primarily focuses on stakeholder alignment and setting the strategic foundation - the critical first steps that enable everything else.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-24 text-center">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-3xl md:text-4xl font-bold text-darkText mb-6">
              Ready to Discuss Your Needs?
            </h3>
            <p className="text-lg text-darkText/80 mb-8">
              Let's talk about your specific challenges and determine the right engagement approach and intensity level for your situation.
            </p>
            <a 
              href="/#contact" 
              className="inline-block bg-cyanPrimary hover:bg-cyanLight text-white font-semibold px-8 py-3 rounded-xl transition-colors"
            >
              Let's Talk
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowWeWorkFinal;